/**
 * Any Career Library API calls need to be on this page.
 * Use CacheKeys QUERY_KEYS.careerLibrary.root to invalidate.
 */
import {
    GetCareer,
    GetCareerList,
    GetCareerListFilters,
    GetLinkableProgrammes,
    GetProgrammeCareerLinks
} from '../../services/CareerLibraryServices';
import { appQueryClient } from '../appQueryClient';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

/**
 * Invalidates all Career Library queries.
 * Linking runs from the PMC pages as well as the career itself, so inactive
 * queries refetch too - the shared hook defaults to refetchOnMount false, which
 * would otherwise serve a stale career the next time it is opened.
 */
export function invalidateCareerLibraryQueries() {
    return appQueryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.careerLibrary.root],
        refetchType: "all"
    });
}

/**
 * Career Library - One career with its tags and linked programmes
 * Not persisted: programme links are also edited from the PMC pages, so a
 * stored copy outlives the edit that changed it.
 * @param {string | number} careerId Career, or falsy while creating a new one.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCareer(careerId) {
    return useBaseReactQuery(
        [QUERY_KEYS.careerLibrary.root, QUERY_KEYS.careerLibrary.career, careerId],
        async () => {
            const { data } = await GetCareer(careerId);
            return data?.data ?? null;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DETAILS,
            persist: false,
            refetchOnMount: true,
            enabled: Boolean(careerId)
        }
    );
}

/**
 * Career Library - Dropdown options for the table filters and detail form - PERSISTED
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCareerListFilters() {
    return useBaseReactQuery(
        [QUERY_KEYS.careerLibrary.root, QUERY_KEYS.careerLibrary.careerFilters],
        async () => {
            const { data } = await GetCareerListFilters();
            return data?.data ?? {};
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.LOOKUP,
            persist: false
        }
    );
}

/**
 * Career Library - Programmes and short courses a career can link to
 * Not persisted - the list spans both management centers and is large.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useLinkableProgrammes() {
    return useBaseReactQuery(
        [QUERY_KEYS.careerLibrary.root, QUERY_KEYS.careerLibrary.linkableProgrammes],
        async () => {
            const { data } = await GetLinkableProgrammes();
            return data?.data ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );
}

/**
 * Career Library - Careers linked to one programme or short course
 * @param {string | number} pmcId Programme or short course id.
 * @param {string} pmcType programme or shortcourse.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useProgrammeCareerLinks(pmcId, pmcType) {
    return useBaseReactQuery(
        [
            QUERY_KEYS.careerLibrary.root,
            QUERY_KEYS.careerLibrary.programmeCareerLinks,
            pmcId,
            pmcType
        ],
        async () => {
            const { data } = await GetProgrammeCareerLinks(pmcId, pmcType);
            return data?.data ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: Boolean(pmcId && pmcType)
        }
    );
}

/**
 * Career Library - Every career, for the PMC career picker
 * Not persisted - the picker needs the current list whenever it opens.
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useAllCareers() {
    return useBaseReactQuery(
        [QUERY_KEYS.careerLibrary.root, QUERY_KEYS.careerLibrary.careerList],
        async () => {
            const { data } = await GetCareerList({ exportStatus: "true" });
            return data?.data_list ?? [];
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.TABLE
        }
    );
}
